import { useMemo, useState } from 'react'
import { BookOpen, ChevronRight, Copy, FileText, GripVertical, Pencil, Trash2 } from 'lucide-react'
import {
  getTextScale,
  getThemePreference,
  setTextScale,
  setThemePreference,
  type LrTextScale,
  type LrThemePreference
} from '../lib/lrUiPreferences'
import { sortRows, useSortState } from '../lib/useSortState'
import {
  Badge,
  Button,
  Chip,
  ChipList,
  EmptyTableRow,
  ListToolbar,
  Modal,
  SortableListItem,
  SortableTh,
  SortableVerticalList,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  Tile,
  TileAccentDot,
  TileAccentStrip,
  TileContent,
  TileDescription,
  TileFooter,
  TileHeader,
  TileMain,
  TileRow,
  TileSquareBody,
  TileTitle
} from '../components/ui'
import type { BadgeVariant } from '../components/ui'

type DemoTableSortKey = 'name' | 'klasse' | 'punkte'

type DemoTableRow = {
  id: string
  name: string
  klasse: string
  punkte: number
  status: 'active' | 'draft' | 'done'
}

const DEMO_TABLE_DATA: DemoTableRow[] = [
  { id: '1', name: 'Müller, Anna', klasse: '10a', punkte: 14, status: 'active' },
  { id: '2', name: 'Schmidt, Ben', klasse: '10a', punkte: 11, status: 'draft' },
  { id: '3', name: 'Weber, Clara', klasse: '9b', punkte: 15, status: 'done' },
  { id: '4', name: 'Fischer, David', klasse: '9b', punkte: 9, status: 'active' }
]

function demoStatusPresentation(status: DemoTableRow['status']): { variant: BadgeVariant; label: string } {
  switch (status) {
    case 'active':
      return { variant: 'success', label: 'Aktiv' }
    case 'draft':
      return { variant: 'warning', label: 'Entwurf' }
    case 'done':
      return { variant: 'neutral', label: 'Erledigt' }
  }
}

type DemoRow = {
  id: string
  title: string
  statusLabel: string
  statusClass: string
  modeLabel: string
  hint: string
}

const codeInline =
  'rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-gray-800 dark:text-gray-200'

export default function UiShowcase() {
  const [basicOpen, setBasicOpen] = useState(false)
  const [footerOpen, setFooterOpen] = useState(false)
  const [skillDemoClicks, setSkillDemoClicks] = useState(0)
  const [theme, setTheme] = useState<LrThemePreference>(() => getThemePreference())
  const [textScale, setTextScaleState] = useState<LrTextScale>(() => getTextScale())
  const [tableSearch, setTableSearch] = useState('')
  const { sortKey, direction, setSort } = useSortState<DemoTableSortKey>('name')

  /** Demo: gleiche Muster wie Kontoauszug-Filter (`PaymentManager`) — `select.input-field` + Dark Mode */
  const [showcasePreset, setShowcasePreset] = useState('all')
  const [showcaseCampaign, setShowcaseCampaign] = useState('')
  const [showcaseBankState, setShowcaseBankState] = useState('')
  const [showcaseCreator, setShowcaseCreator] = useState('')
  const [showcaseFreetext, setShowcaseFreetext] = useState('')
  const [settingsAreaDemo, setSettingsAreaDemo] = useState<'personal' | 'school' | 'system'>('personal')
  const [settingsSubtabDemo, setSettingsSubtabDemo] = useState('personalData')
  const [chipDemoRecipients, setChipDemoRecipients] = useState(() => [
    'Mustermann, Max',
    'Sekretariat',
    'projekt@schule.de'
  ])

  const [demoRows, setDemoRows] = useState<DemoRow[]>(() => [
    {
      id: 'cw-1',
      title: 'Beispiel Klassenarbeit',
      statusLabel: 'Aktiv',
      statusClass:
        'bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800',
      modeLabel: 'Papier/Offline',
      hint: 'Ergebnisse importieren'
    },
    {
      id: 'cw-2',
      title: 'Demo Aufgabe Digital',
      statusLabel: 'Entwurf',
      statusClass:
        'bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600',
      modeLabel: 'Digital',
      hint: 'Weiter bearbeiten'
    },
    {
      id: 'cw-3',
      title: 'Drittes Element',
      statusLabel: 'Aktiv',
      statusClass:
        'bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800',
      modeLabel: 'Digital',
      hint: 'Abgaben prüfen'
    }
  ])

  const demoIds = useMemo(() => demoRows.map((r) => r.id), [demoRows])

  const visibleTableRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase()
    let rows = DEMO_TABLE_DATA
    if (q) {
      rows = rows.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.klasse.toLowerCase().includes(q) ||
          String(r.punkte).includes(q)
      )
    }
    return sortRows(rows, sortKey, direction)
  }, [tableSearch, sortKey, direction])

  const themeOptions: { value: LrThemePreference; label: string }[] = [
    { value: 'light', label: 'Hell' },
    { value: 'dark', label: 'Dunkel' },
    { value: 'system', label: 'System' }
  ]
  const scaleOptions: { value: LrTextScale; label: string }[] = [
    { value: 'xs', label: 'Sehr klein' },
    { value: 'sm', label: 'Klein (S)' },
    { value: 'md', label: 'Mittel (M)' },
    { value: 'lg', label: 'Groß (L)' },
    { value: 'xl', label: 'Sehr groß' }
  ]

  return (
    <div className="mx-auto max-w-4xl space-y-10 px-4 py-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">UI Showcase</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Referenz für globale Komponenten und Listen-/Tabellen-Vorlagen (<code className={codeInline}>Button</code>,{' '}
          <code className={codeInline}>Modal</code>, <code className={codeInline}>Tile</code>,{' '}
          <code className={codeInline}>input-field</code> / <code className={codeInline}>select</code>, verschiebbare
          Listen, <code className={codeInline}>TableScrollArea</code>, <code className={codeInline}>Badge</code>,{' '}
          <code className={codeInline}>Chip</code>, …). Nur
          zur Design-Prüfung.
        </p>
      </div>

      <section className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-50">Darstellung</h2>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Vorschau für Hell/Dunkel/System und Schriftgröße — Einstellungen werden lokal gespeichert (
          <code className={codeInline}>lr-ui-theme</code>, <code className={codeInline}>lr-ui-text-scale</code>) und auf{' '}
          <code className={codeInline}>document.documentElement</code> angewendet.
        </p>
        <div className="flex flex-col gap-6 sm:flex-row sm:flex-wrap">
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Farbschema</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {themeOptions.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  size="sm"
                  variant={theme === opt.value ? 'primary' : 'outline'}
                  onClick={() => {
                    setThemePreference(opt.value)
                    setTheme(opt.value)
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Schriftgröße</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {scaleOptions.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  size="sm"
                  variant={textScale === opt.value ? 'primary' : 'outline'}
                  onClick={() => {
                    setTextScale(opt.value)
                    setTextScaleState(opt.value)
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-50">Einstellungen: Bereichs-Navigation</h2>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Muster fuer mehrstufige Navigation in den Einstellungen: obere Bereichsauswahl als Segment-Buttons und darunter
          Unterpunkte ebenfalls als <code className={codeInline}>Button size="sm"</code> mit{' '}
          <code className={codeInline}>primary/outline</code>.
        </p>
        <div className="mb-3 flex flex-wrap gap-2">
          {[
            { id: 'personal', label: 'Persoenlich & Konto' },
            { id: 'school', label: 'Schule & Organisation' },
            { id: 'system', label: 'System & Administration' }
          ].map((area) => (
            <Button
              key={area.id}
              size="sm"
              variant={settingsAreaDemo === area.id ? 'primary' : 'outline'}
              onClick={() => {
                setSettingsAreaDemo(area.id as 'personal' | 'school' | 'system')
                setSettingsSubtabDemo(
                  area.id === 'personal' ? 'personalData' : area.id === 'school' ? 'users' : 'systemGeneral'
                )
              }}
            >
              {area.label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {(settingsAreaDemo === 'personal'
            ? [
                { id: 'personalData', label: 'Persoenliche Daten' },
                { id: 'appearance', label: 'Darstellung' },
                { id: 'security', label: 'Sicherheit' },
                { id: 'mailSignature', label: 'E-Mail-Signatur' }
              ]
            : settingsAreaDemo === 'school'
              ? [
                  { id: 'schoolGeneral', label: 'Schulweite Einstellungen' },
                  { id: 'users', label: 'Benutzerverwaltung' },
                  { id: 'grades', label: 'Notenschemata' }
                ]
              : [
                  { id: 'systemGeneral', label: 'Systemeinstellungen' },
                  { id: 'payments', label: 'Geldkonten' },
                  { id: 'maintenance', label: 'Wartung' }
                ]
          ).map((subtab) => (
            <Button
              key={subtab.id}
              size="sm"
              variant={settingsSubtabDemo === subtab.id ? 'primary' : 'outline'}
              onClick={() => setSettingsSubtabDemo(subtab.id)}
            >
              {subtab.label}
            </Button>
          ))}
        </div>
      </section>

      <section className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-50">Security/MFA Demo</h2>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Referenz fuer die neue Sicherheit-Seite (2FA-Status + vertraute Geraete).
        </p>
        <div className="grid gap-4 lg:grid-cols-2">
          <Tile>
            <TileHeader>
              <TileTitle as="h3">2FA Status</TileTitle>
              <TileDescription>Authenticator + E-Mail-OTP moeglich</TileDescription>
            </TileHeader>
            <TileContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="success">Aktiviert</Badge>
                <span className="text-xs text-gray-600 dark:text-gray-300">h***o@schule.de</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm">Authenticator einrichten</Button>
                <Button size="sm" variant="outline">E-Mail-Code</Button>
              </div>
            </TileContent>
          </Tile>
          <Tile>
            <TileHeader>
              <TileTitle as="h3">Trusted Device Policy</TileTitle>
              <TileDescription>Sliding 60 Tage, Hard-Cap 180 Tage</TileDescription>
            </TileHeader>
            <TileContent>
              <div className="space-y-2 text-xs text-gray-600 dark:text-gray-300">
                <p>Erste Nutzung: voller 2FA-Schritt.</p>
                <p>Bekanntes Geraet: Username + Passwort reicht.</p>
                <p>Admin kann Pflicht pro Rolle global steuern.</p>
              </div>
            </TileContent>
          </Tile>
        </div>

        <div className="mt-4">
          <TableScrollArea>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Geraet</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Zuletzt aktiv</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>MacBook Lehrerzimmer</TableCell>
                  <TableCell><Badge variant="info">Aktuell</Badge></TableCell>
                  <TableCell>Heute, 08:14</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>iPad privat</TableCell>
                  <TableCell><Badge variant="neutral">Vertraut</Badge></TableCell>
                  <TableCell>Gestern, 21:40</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableScrollArea>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-50">Master-Detail: Schueler-Layout</h2>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Referenz fuer <code className={codeInline}>/students-v2</code>: Toolbar, linke Liste, rechte Detailflaeche und
          zweistufige Segment-Buttons.
        </p>
        <ListToolbar
          title="Schueler"
          description="Master-Detail mit voller Breite und Segment-Navigation."
          trailing={
            <>
              <input
                type="search"
                className="input-field min-h-[2.25rem] py-2 text-sm"
                placeholder="Name, Vorname oder Klasse …"
              />
              <Button size="sm">Hinzufuegen</Button>
              <Button size="sm" variant="outline">Liste ausblenden</Button>
            </>
          }
        />
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(300px,380px)_1fr]">
          <Tile>
            <TileHeader>
              <TileTitle as="h3">Schuelerliste</TileTitle>
            </TileHeader>
            <TileContent className="space-y-2">
              <div className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-900 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-100">
                Mustermann, Anna · 10a
              </div>
              <div className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
                Beispiel, Ben · 9b
              </div>
              <div className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
                Demo, Clara · 8c
              </div>
            </TileContent>
          </Tile>
          <Tile>
            <TileHeader className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button size="sm">Stammdaten</Button>
                <Button size="sm" variant="outline">Leistung</Button>
                <Button size="sm" variant="outline">Anwesenheit</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm">Profil & Zugriff</Button>
                <Button size="sm" variant="outline">Kontakte, E-Mail & Akte</Button>
              </div>
            </TileHeader>
            <TileContent>
              <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">
                Rechte Detailansicht zeigt nur den aktiven Unterbereich.
              </p>
              <Badge variant="info">Segment-Muster fuer Schueler-V2</Badge>
            </TileContent>
          </Tile>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-50">Formularfelder (`input-field`)</h2>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Textfelder und native <code className={codeInline}>&lt;select&gt;</code> nutzen{' '}
          <code className={codeInline}>className=&quot;input-field …&quot;</code>. Dark-Mode für Selects (Hintergrund,
          Chevron, <code className={codeInline}>color-scheme</code>, Option-Farben) ist zentral in{' '}
          <code className={codeInline}>src/index.css</code> unter <code className={codeInline}>select.input-field</code>{' '}
          definiert — bitte nicht mit zusätzlichem <code className={codeInline}>bg-white</code> ohne{' '}
          <code className={codeInline}>dark:</code> überschreiben. Zeile unten entspricht dem Filterband «Kontoauszug &amp;
          Zuordnungen».
        </p>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="flex min-w-[11rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
            Schnellfilter
            <select
              value={showcasePreset}
              onChange={(e) => setShowcasePreset(e.target.value)}
              className="input-field min-h-[2.25rem] py-2 text-sm"
            >
              <option value="all">Alle Posten</option>
              <option value="lr_likely">Mutmaßlich Lernrakete (Ref. LR …)</option>
              <option value="matching_open">Zuordnung offen / Vorschlag</option>
              <option value="linked">Mit Forderung verbunden</option>
            </select>
          </label>
          <label className="flex min-w-[12rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
            Vorgang
            <select
              value={showcaseCampaign}
              onChange={(e) => setShowcaseCampaign(e.target.value)}
              className="input-field min-h-[2.25rem] py-2 text-sm"
            >
              <option value="">Alle Vorgänge</option>
              <option value="1">Klassenfahrt 10a</option>
              <option value="2">Materialbeitrag Q2</option>
            </select>
          </label>
          <label className="flex min-w-[10rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
            Ersteller (Nutzername)
            <input
              type="search"
              value={showcaseCreator}
              onChange={(e) => setShowcaseCreator(e.target.value)}
              placeholder="z. B. max.mustermann"
              className="input-field min-h-[2.25rem] py-2 text-sm"
              autoComplete="off"
            />
          </label>
          <label className="flex min-w-[12rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
            Freitext (Zahler, Zweck, IBAN…)
            <input
              type="search"
              value={showcaseFreetext}
              onChange={(e) => setShowcaseFreetext(e.target.value)}
              placeholder="Suche in Buchung"
              className="input-field min-h-[2.25rem] py-2 text-sm"
              autoComplete="off"
            />
          </label>
          <label className="flex min-w-[9rem] flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
            Bank-Status
            <select
              value={showcaseBankState}
              onChange={(e) => setShowcaseBankState(e.target.value)}
              className="input-field min-h-[2.25rem] py-2 text-sm"
            >
              <option value="">Alle Stati</option>
              <option value="unprocessed">Offen</option>
              <option value="suggested">Vorschlag</option>
              <option value="partially_allocated">Teilweise</option>
              <option value="fully_allocated">Verbucht</option>
              <option value="ignored">Ignoriert</option>
            </select>
          </label>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Zum Prüfen: oben «Dunkel» wählen und jedes Dropdown öffnen — Liste und gewählter Text müssen gut lesbar sein.
        </p>
      </section>

      <section className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-50">Listen &amp; Tabellen</h2>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Vorlage für Admin-Listen:{' '}
          <code className={codeInline}>ListToolbar</code> (Titel + Suche/Filter rechts),{' '}
          <code className={codeInline}>TableScrollArea</code> / <code className={codeInline}>Table*</code>, sortierbare
          Kopfzeile mit <code className={codeInline}>SortableTh</code>, Sortierlogik mit{' '}
          <code className={codeInline}>useSortState</code> und <code className={codeInline}>sortRows</code> aus{' '}
          <code className={codeInline}>src/lib/useSortState.ts</code>, Status mit <code className={codeInline}>Badge</code>,
          Leerzustand mit <code className={codeInline}>EmptyTableRow</code> / <code className={codeInline}>EmptyState</code>.
          Tags und entfernbare Auswahl: <code className={codeInline}>Chip</code> / <code className={codeInline}>ChipList</code>.
        </p>

        <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">Badge</p>
        <div className="mb-6 flex flex-wrap gap-2">
          <Badge variant="neutral">neutral</Badge>
          <Badge variant="success">Erfolg</Badge>
          <Badge variant="warning">Warnung</Badge>
          <Badge variant="danger">Fehler</Badge>
          <Badge variant="info">Info</Badge>
        </div>

        <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">Chip &amp; ChipList</p>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Tags, Empfänger, Filter — optional mit Entfernen-Button. Tabellen- und Prozess-Status bleiben bei{' '}
          <code className={codeInline}>Badge</code>.
        </p>
        <div className="mb-3 flex flex-wrap gap-2">
          <Chip variant="neutral">neutral</Chip>
          <Chip variant="primary">primary</Chip>
          <Chip variant="outline">outline</Chip>
          <Chip variant="danger">danger</Chip>
          <Chip variant="success">success</Chip>
          <Chip variant="warning">warning</Chip>
          <Chip variant="primary" size="md">
            Größe md
          </Chip>
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Entfernen (Demo):</span>
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => setChipDemoRecipients(['Mustermann, Max', 'Sekretariat', 'projekt@schule.de'])}
          >
            Zurücksetzen
          </Button>
        </div>
        <ChipList
          className="mb-4"
          empty="Keine Empfänger in der Demo — „Zurücksetzen“ nutzen."
        >
          {chipDemoRecipients.map((label, i) => (
            <Chip
              key={`chip-${i}-${label}`}
              variant="primary"
              title={label}
              onRemove={() => setChipDemoRecipients((prev) => prev.filter((_, j) => j !== i))}
              removeLabel={`${label} entfernen`}
            >
              {label}
            </Chip>
          ))}
        </ChipList>
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">ChipList mit leerem Inhalt:</p>
        <ChipList className="mb-6 rounded-md border border-dashed border-gray-200 px-3 py-2 dark:border-gray-600" empty="Keine Chips" />

        <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">Demo-Tabelle (Suche + Sortierung)</p>
        <ListToolbar
          title="Beispiel-Liste"
          description="Lokale Suche und Klicks auf die Spaltenköpfe — so können künftige Seiten auf dieselbe Struktur migrieren."
          trailing={
            <>
              <label htmlFor="showcase-table-search" className="sr-only">
                Suche in der Beispieltabelle
              </label>
              <input
                id="showcase-table-search"
                type="search"
                placeholder="Suche …"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                className="input-field max-w-xs min-h-[2.25rem] py-2 text-sm"
                autoComplete="off"
              />
            </>
          }
        />

        <TableScrollArea>
          <Table>
            <TableHead>
              <TableRow>
                <SortableTh
                  sortKey="name"
                  activeKey={sortKey}
                  direction={direction}
                  onSort={setSort}
                  className="whitespace-normal"
                >
                  Name
                </SortableTh>
                <SortableTh sortKey="klasse" activeKey={sortKey} direction={direction} onSort={setSort}>
                  Klasse
                </SortableTh>
                <SortableTh sortKey="punkte" activeKey={sortKey} direction={direction} onSort={setSort}>
                  Punkte
                </SortableTh>
                <TableHeaderCell className="whitespace-normal">Status</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTableRows.length === 0 ? (
                <EmptyTableRow
                  colSpan={4}
                  title="Keine Einträge"
                  description={
                    tableSearch.trim()
                      ? 'Kein Treffer für die aktuelle Suche.'
                      : 'Es sind keine Zeilen zum Anzeigen vorhanden.'
                  }
                />
              ) : (
                visibleTableRows.map((row) => {
                  const st = demoStatusPresentation(row.status)
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="max-w-[14rem] whitespace-normal">{row.name}</TableCell>
                      <TableCell className="whitespace-normal">{row.klasse}</TableCell>
                      <TableCell align="right">{row.punkte}</TableCell>
                      <TableCell>
                        <Badge variant={st.variant}>{st.label}</Badge>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableScrollArea>

        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          Vertikal verschiebbare <strong>Karten</strong> bleiben bei{' '}
          <code className={codeInline}>SortableVerticalList</code> — Tabellen nutzen{' '}
          <code className={codeInline}>SortableTh</code> + <code className={codeInline}>sortRows</code>, keine @dnd-kit
          auf der <code className={codeInline}>&lt;table&gt;</code>, außer wo bereits eigenes Row-DnD existiert (z.&nbsp;B.
          Curricula).
        </p>
      </section>

      <section className="card p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-50">Button</h2>
        <div className="flex flex-wrap gap-3">
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="ghost">Ghost</Button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button size="xs">xs</Button>
          <Button size="sm">sm</Button>
          <Button size="md">md</Button>
          <Button size="lg">lg</Button>
        </div>
        <div className="mt-4">
          <Button disabled>Disabled</Button>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-50">Modal</h2>
        <div className="flex flex-wrap gap-3">
          <Button type="button" onClick={() => setBasicOpen(true)}>
            Modal öffnen
          </Button>
          <Button type="button" variant="secondary" onClick={() => setFooterOpen(true)}>
            Modal mit Footer
          </Button>
        </div>

        <Modal isOpen={basicOpen} onClose={() => setBasicOpen(false)} title="Beispiel-Dialog" size="md">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Inhalt des Dialogs. Schließen über X, Escape oder Klick auf die Fläche außerhalb.
          </p>
        </Modal>

        <Modal
          isOpen={footerOpen}
          onClose={() => setFooterOpen(false)}
          title="Mit Aktionen"
          size="lg"
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setFooterOpen(false)}>
                Abbrechen
              </Button>
              <Button type="button" variant="primary" onClick={() => setFooterOpen(false)}>
                OK
              </Button>
            </div>
          }
        >
          <p className="text-sm text-gray-700 dark:text-gray-300">Footer mit Standard-Buttons für Bestätigung.</p>
        </Modal>
      </section>

      <section className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-50">Kachel (Tile)</h2>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Vorlage für KPI-Kacheln, Lerngruppen-Karten, Skillmap-Quadrate, Lernbüro-Aufgaben und Zeilen mit
          Akzent-Streifen — später bestehende Einzelimplementierungen hierhin migrieren.
        </p>

        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          Die Lehrer-Übersicht unter <code className={codeInline}>/dashboard</code> nutzt dieselbe Struktur (KPI-Zeile
          mit <code className={codeInline}>Tile variant=&quot;compact&quot;</code>, Bereiche mit{' '}
          <code className={codeInline}>TileHeader</code> / <code className={codeInline}>ListToolbar</code>). Im Layout
          ist für diese Route <code className={codeInline}>max-w-none</code> gesetzt — volle Content-Breite neben der
          Sidebar.
        </p>

        <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">KPI / kompakt</p>
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { n: '128', l: 'Schüler' },
            { n: '12', l: 'Bausteine' },
            { n: '4', l: 'Sitzungen' },
            { n: '7', l: 'Offen' }
          ].map((k) => (
            <Tile key={k.l} variant="compact">
              <TileContent className="py-4 text-center">
                <p className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">{k.n}</p>
                <TileDescription className="mt-0.5">{k.l}</TileDescription>
              </TileContent>
            </Tile>
          ))}
        </div>

        <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">Mit Kopf &amp; Fuß (z. B. Lerngruppe)</p>
        <Tile className="mb-6 max-w-md">
          <TileHeader className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-600 text-white">
              <BookOpen className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0">
              <TileTitle className="text-base">Oberstufen-Kurs</TileTitle>
              <TileDescription>Mathematik · Jahrgang 11</TileDescription>
            </div>
          </TileHeader>
          <TileContent>
            <p className="text-sm text-gray-600 dark:text-gray-400">Teilnehmer: 18 / 24</p>
          </TileContent>
          <TileFooter className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary">
              Bearbeiten
            </Button>
            <Button type="button" size="sm" variant="outline">
              Teilnehmer
            </Button>
          </TileFooter>
        </Tile>

        <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">Skillmap-Quadrat (interaktiv)</p>
        <div className="mb-6 grid max-w-md grid-cols-2 gap-3">
          <Tile
            variant="interactive"
            onClick={() => setSkillDemoClicks((c) => c + 1)}
            aria-label="Demo-Kachel"
          >
            <TileAccentDot colorClassName="bg-emerald-500" />
            <TileSquareBody>
              <span className="text-2xl" aria-hidden>
                ◆
              </span>
              <TileTitle className="mt-2 line-clamp-3 text-xs font-normal leading-snug">
                Kompetenzbezeichnung im Raster
              </TileTitle>
              <TileDescription className="mt-1">Klicks: {skillDemoClicks}</TileDescription>
            </TileSquareBody>
          </Tile>
          <Tile variant="surface">
            <TileAccentDot accentHex="#2563eb" />
            <TileSquareBody>
              <TileTitle className="line-clamp-3 text-xs font-medium">
                Punkt mit API-Farbe (accentHex)
              </TileTitle>
              <TileDescription className="mt-1">nur bei Lernplan-Farben</TileDescription>
            </TileSquareBody>
          </Tile>
        </div>

        <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">Zeile mit Akzent-Streifen</p>
        <Tile className="mb-6 max-w-xl">
          <TileRow>
            <TileAccentStrip>
              <FileText className="h-5 w-5 opacity-95" aria-hidden />
            </TileAccentStrip>
            <TileMain className="flex items-center px-4 py-3">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-50">
                Wie Klassenarbeit-Liste — Inhalt rechts vom Streifen
              </span>
            </TileMain>
          </TileRow>
        </Tile>
      </section>

      <section className="card p-6">
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-50">Verschiebbare Liste</h2>
        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
          Gleicher Ansatz wie Bausteine (Lernpläne/Aufgaben), Curricula und künftige sortierbare Klassenarbeiten:{' '}
          <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px] dark:bg-gray-800 dark:text-gray-200">
            SortableVerticalList
          </code>{' '}
          +{' '}
          <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px] dark:bg-gray-800 dark:text-gray-200">
            SortableListItem
          </code>{' '}
          (@dnd-kit). Griff nur auf der linken Leiste; Reihenfolge ist hier nur lokal (Demo).
        </p>

        <SortableVerticalList itemIds={demoIds} onReorder={(next) => setDemoRows((prev) => sortRowsByIds(prev, next))}>
          <div className="space-y-3">
            {demoRows.map((item) => (
              <SortableListItem key={item.id} id={item.id}>
                {({ setNodeRef, style, dragHandleProps }) => (
                  <Tile
                    ref={setNodeRef}
                    style={style}
                    variant="surface"
                    className="transition-all hover:shadow-md"
                  >
                    <TileRow>
                      <button
                        type="button"
                        className="flex w-10 shrink-0 cursor-grab items-center justify-center rounded-l-lg border-0 bg-gradient-to-b from-slate-500 to-slate-600 p-0 text-white active:cursor-grabbing"
                        aria-label="Ziehen zum Sortieren"
                        {...dragHandleProps}
                      >
                        <GripVertical className="h-5 w-5 drop-shadow-sm" aria-hidden />
                      </button>
                      <TileMain className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
                        <div className="min-w-0 flex-1 text-left">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900 dark:text-gray-50">{item.title}</span>
                            <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${item.statusClass}`}
                            >
                              {item.statusLabel}
                            </span>
                            <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-800 dark:border-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200">
                              {item.modeLabel}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{item.hint}</span>
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2 border-t border-gray-100 pt-2 dark:border-gray-700 sm:border-t-0 sm:pt-0">
                          <Button type="button" size="sm" variant="primary">
                            Nächste Aktion
                          </Button>
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              type="button"
                              title="Bearbeiten"
                              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-950/40 dark:hover:text-primary-300"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="sr-only">Bearbeiten</span>
                            </button>
                            <button
                              type="button"
                              title="Duplizieren"
                              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-300"
                            >
                              <Copy className="h-4 w-4" />
                              <span className="sr-only">Duplizieren</span>
                            </button>
                            <button
                              type="button"
                              title="Löschen"
                              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Löschen</span>
                            </button>
                          </div>
                        </div>
                      </TileMain>
                    </TileRow>
                  </Tile>
                )}
              </SortableListItem>
            ))}
          </div>
        </SortableVerticalList>
      </section>
    </div>
  )
}

function sortRowsByIds(rows: DemoRow[], orderedIds: string[]): DemoRow[] {
  const map = new Map(rows.map((r) => [r.id, r]))
  return orderedIds.map((id) => map.get(id)).filter(Boolean) as DemoRow[]
}
